import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React from 'react';

/**
 * The pictures of the ACH connection guide, drawn with plain elements instead of screenshots: they carry no borrower
 * names or loan numbers, follow the app theme, and change with a one-line edit. Each one is a simplified copy of the
 * real screen, with the thing to look at circled.
 */
const useArt = makeStyles((theme: Theme) => ({
  win: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: 10,
    fontSize: 11,
    lineHeight: 1.45,
    color: theme.palette.text.secondary,
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  strong: { color: theme.palette.text.primary, fontWeight: 600 },
  tabs: { display: 'flex', gap: 14, marginBottom: 8 },
  note: {
    background: theme.palette.action.hover,
    color: theme.palette.primary.main,
    borderRadius: 6,
    padding: '6px 8px',
    marginBottom: 6,
  },
  bar: { height: 6, borderRadius: 3, background: theme.palette.divider, margin: '5px 0', width: '75%' },
  row: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  btn: {
    display: 'inline-block',
    padding: '5px 10px',
    borderRadius: 6,
    fontSize: 11,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
  },
  btnPrimary: {
    background: theme.palette.text.primary,
    color: theme.palette.background.paper,
    borderColor: theme.palette.text.primary,
  },
  block: { display: 'block', textAlign: 'center' },
  dim: { opacity: 0.55 },
  hl: { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  card: { border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: 10, textAlign: 'left' },
  field: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: '4px 6px',
    marginBottom: 5,
  },
  muted: { color: theme.palette.text.disabled },
  plaid: {
    border: `1px solid ${theme.palette.text.disabled}`,
    borderRadius: 8,
    padding: 8,
    background: theme.palette.background.paper,
    minWidth: 0,
  },
  ways: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 22px minmax(0, 1fr)', gap: 6, alignItems: 'center' },
  or: { textAlign: 'center', fontSize: 12 },
  radio: {
    flex: '0 0 12px',
    height: 12,
    borderRadius: '50%',
    border: `1px solid ${theme.palette.text.disabled}`,
    marginTop: 1,
  },
  radioOn: { background: theme.palette.text.primary, borderColor: theme.palette.text.primary },
  account: {
    display: 'flex',
    gap: 6,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: 6,
    marginBottom: 5,
  },
  warn: {
    border: `1px solid ${theme.palette.warning.main}`,
    background: theme.palette.action.hover,
    color: theme.palette.text.primary,
    borderRadius: 8,
    padding: 8,
  },
  fallback: { display: 'grid', gridTemplateColumns: '1fr 20px 1fr', gap: 6, alignItems: 'center' },
  tagRow: { display: 'flex', alignItems: 'center', gap: 6 },
  tag: {
    flex: '0 0 16px',
    height: 16,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontSize: 10,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 5,
  },
  tiles: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 },
  tile: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: '9px 4px',
    textAlign: 'center',
    color: theme.palette.text.primary,
  },
  option: { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' },
  box: {
    flex: '0 0 12px',
    height: 12,
    borderRadius: 2,
    border: `1px solid ${theme.palette.text.disabled}`,
    fontSize: 10,
    lineHeight: '10px',
    textAlign: 'center',
  },
  boxOn: { background: theme.palette.primary.main, borderColor: theme.palette.primary.main, color: theme.palette.primary.contrastText },
}));

type BtnProps = { children: React.ReactNode; primary?: boolean; hl?: boolean; dim?: boolean; block?: boolean };

const Btn = ({ children, primary, hl, dim, block }: BtnProps) => {
  const c = useArt();
  return (
    <span className={clsx(c.btn, primary && c.btnPrimary, hl && c.hl, dim && c.dim, block && c.block)}>{children}</span>
  );
};

const Field = ({ children, hl, muted }: { children: React.ReactNode; hl?: boolean; muted?: boolean }) => {
  const c = useArt();
  return <div className={clsx(c.field, hl && c.hl, muted && c.muted)}>{children}</div>;
};

const Tag = ({ children }: { children: React.ReactNode }) => {
  const c = useArt();
  return <span className={c.tag}>{children}</span>;
};

const Option = ({ children, on, hl, dim }: { children: React.ReactNode; on?: boolean; hl?: boolean; dim?: boolean }) => {
  const c = useArt();
  return (
    <div className={clsx(c.option, hl && c.hl, dim && c.dim)}>
      <span className={clsx(c.box, on && c.boxOn)}>{on ? '\u2713' : ''}</span>
      <span>{children}</span>
    </div>
  );
};

/** Step 1 (staff): the ACH Consent tab with Create Link circled. */
export const CreateLinkArt = () => {
  const c = useArt();
  return (
    <div className={c.win}>
      <div className={c.tabs}>
        <span className={c.strong}>ACH Consent</span>
        <span>Chat</span>
        <span>Notes</span>
      </div>
      <div className={c.note}>The Plaid link for the borrower was created and expires soon. It has not been opened yet.</div>
      <div className={c.bar} />
      <div className={c.strong} style={{ margin: '12px 0 4px' }}>
        Select link delivery method
      </div>
      <div className={c.row}>
        <Btn hl>Create Link</Btn>
        <Btn>Send Email</Btn>
        <Btn dim>Create Manual Link</Btn>
      </div>
    </div>
  );
};

/** Step 2 (borrower): the welcome page of the secure link. */
export const OpenLinkArt = () => {
  const c = useArt();
  return (
    <div className={clsx(c.win, c.center)}>
      <div className={c.card} style={{ width: '62%' }}>
        <div className={c.strong} style={{ fontSize: 15 }}>
          Hi Alex
        </div>
        <div style={{ margin: '2px 0 8px' }}>Your lender sent you a secure link.</div>
        <div className={c.field} style={{ marginBottom: 8 }}>
          Set up your ACH
        </div>
        <Btn primary hl block>
          Continue
        </Btn>
      </div>
    </div>
  );
};

/** Step 3 (borrower): Plaid's first screen, with the two ways forward. */
export const StartPlaidArt = () => {
  const c = useArt();
  return (
    <div className={clsx(c.win, c.center)}>
      <div className={c.plaid} style={{ width: '58%' }}>
        <div className={c.strong} style={{ textAlign: 'center', marginBottom: 5 }}>
          Plaid
        </div>
        <div className={c.strong} style={{ marginBottom: 6 }}>
          Your lender uses Plaid to connect your account
        </div>
        <div className={c.tagRow}>
          <Tag>A</Tag>
          <div style={{ flex: 1 }}>
            <Field hl>+1 Phone number</Field>
          </div>
        </div>
        <div className={c.dim}>
          <Btn primary block>
            Continue
          </Btn>
        </div>
        <div className={c.tagRow} style={{ marginTop: 8, justifyContent: 'center' }}>
          <Tag>B</Tag>
          <span className={clsx(c.strong, c.hl)} style={{ padding: '2px 4px', marginBottom: 5 }}>
            Continue without phone number
          </span>
        </div>
      </div>
    </div>
  );
};

/** Step 4 (borrower): Plaid's institution list. */
export const InstitutionArt = () => {
  const c = useArt();
  const tiles = ['Bank A', 'Bank B', 'Bank C', 'Bank D', 'Bank E', 'Bank F'];
  return (
    <div className={clsx(c.win, c.center)}>
      <div className={c.plaid} style={{ width: '58%' }}>
        <div className={c.strong} style={{ marginBottom: 6 }}>
          Select your institution
        </div>
        <Field muted>Search</Field>
        <div className={c.tiles}>
          {tiles.map((name, index) => (
            <div key={name} className={clsx(c.tile, index === 1 && c.hl)}>
              {name}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** Step 5 (borrower): the bank's own sign-in window, opened by Continue to log in. */
export const SignInArt = () => {
  const c = useArt();
  return (
    <div className={clsx(c.win, c.center)}>
      <div className={c.plaid} style={{ width: '48%' }}>
        <div className={c.strong} style={{ marginBottom: 2 }}>
          Your bank
        </div>
        <div style={{ marginBottom: 8 }}>Opens in its own window</div>
        <Field hl>Username</Field>
        <Field hl>Password</Field>
        <Btn primary block>
          Sign in
        </Btn>
      </div>
    </div>
  );
};

/** Step 6 (borrower): choosing the one account, on the bank's own page or on Plaid's list. */
export const ChooseAccountArt = () => {
  const c = useArt();
  return (
    <div className={clsx(c.win, c.ways)}>
      <div>
        <div className={c.strong} style={{ marginBottom: 5 }}>
          Banks with their own page
        </div>
        <div className={c.plaid}>
          <div className={c.strong} style={{ marginBottom: 3 }}>
            1. Select the account to share
          </div>
          <Option on hl>
            Checking {'\u2022\u2022\u2022\u2022'}0000
          </Option>
          <Option>Savings {'\u2022\u2022\u2022\u2022'}1111</Option>
          <div className={c.strong} style={{ margin: '6px 0 3px' }}>
            2. Allow access
          </div>
          <Option on hl>Account and routing number</Option>
          <Option on>Account holder names and roles</Option>
          <Option on>Profile information</Option>
          <div style={{ marginTop: 6 }}>
            <Btn primary>Continue</Btn>
          </div>
        </div>
      </div>
      <div className={c.or}>or</div>
      <div>
        <div className={c.strong} style={{ marginBottom: 5 }}>
          Other banks
        </div>
        <div className={c.plaid}>
          <div className={c.strong} style={{ marginBottom: 2 }}>
            Select an account
          </div>
          <div style={{ marginBottom: 5 }}>Plaid will only share data from the selected account.</div>
          <div className={clsx(c.account, c.hl)}>
            <span className={clsx(c.radio, c.radioOn)} />
            <span>Checking {'\u2022\u2022\u2022\u2022'}0000</span>
          </div>
          <div className={c.account}>
            <span className={c.radio} />
            <span>Savings {'\u2022\u2022\u2022\u2022'}1111</span>
          </div>
          <Btn primary block>
            Confirm
          </Btn>
        </div>
      </div>
    </div>
  );
};

/** Step 5 (borrower): back on the form, with the chosen bank. */
export const ConfirmArt = () => {
  const c = useArt();
  return (
    <div className={c.win}>
      <div className={c.card} style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
        <span>
          <span className={c.strong}>Your bank</span>
          <br />
          Checking {'••••'} 0000
        </span>
        <span style={{ textDecoration: 'underline' }}>Change</span>
      </div>
      <div style={{ marginBottom: 2 }}>Recurring monthly payment date *</div>
      <Field hl>1st day of the month</Field>
      <Btn primary>Continue</Btn>
    </div>
  );
};

/** Step 6 (staff): what the borrower sees when Plaid can't be used, and the button that fixes it. */
export const ManualFallbackArt = () => {
  const c = useArt();
  return (
    <div className={clsx(c.win, c.fallback)}>
      <div className={c.warn}>
        <b>The borrower sees</b>
        <br />
        We couldn't connect this account. Please contact your lender to enter your bank details manually.
      </div>
      <span aria-hidden="true" style={{ textAlign: 'center', fontSize: 16 }}>
        {'→'}
      </span>
      <div className={c.card}>
        <div className={c.strong}>You, on the task</div>
        <div className={c.row} style={{ marginTop: 8 }}>
          <Btn>Create Link</Btn>
          <Btn hl>Create Manual Link</Btn>
        </div>
      </div>
    </div>
  );
};

/** Last step of the borrower guide: the message when Plaid can't be used, and the new link from the lender. */
export const BorrowerFallbackArt = () => {
  const c = useArt();
  return (
    <div className={clsx(c.win, c.fallback)}>
      <div className={c.warn}>
        <b>You see</b>
        <br />
        We couldn't connect this account. Please contact your lender to enter your bank details manually.
      </div>
      <span aria-hidden="true" style={{ textAlign: 'center', fontSize: 16 }}>
        {'\u2192'}
      </span>
      <div className={c.card}>
        <div className={c.strong}>New link from your lender</div>
        <div style={{ margin: '6px 0 4px' }}>Enter your bank details</div>
        <Field>Account number</Field>
        <Field>Routing number</Field>
        <Btn primary hl>
          Continue
        </Btn>
      </div>
    </div>
  );
};
